import React, { useState } from 'react';
import { GoInfo } from "react-icons/go";
import { ErrorBox } from './common/ErrorBox';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useFormContext } from 'react-hook-form';

const DimensionPicker = ({
  name,
  label,
  title,
  errors,
  detailed_label,
  validation,
  fields,
  defaultValue,
  displayUnit = null,
  conversionFactor = 1
}) => {
  const { register, setValue, getValues } = useFormContext();

  // Convert backend value to display value
  const backendToDisplay = (value) => {
    if (!value) return '';
    return (parseFloat(value) / conversionFactor).toString();
  };

  // Convert display value to backend value
  const displayToBackend = (value) => {
    if (!value) return '';
    return (parseFloat(value) * conversionFactor).toString();
  };

  // Local state for display values
  const [displayWidth, setDisplayWidth] = useState(() => {
    const backendValue = getValues(`${name}.WIDTH`) || defaultValue?.WIDTH || '';
    return backendToDisplay(backendValue);
  });

  const [displayHeight, setDisplayHeight] = useState(() => {
    const backendValue = getValues(`${name}.HEIGHT`) || defaultValue?.HEIGHT || '';
    return backendToDisplay(backendValue);
  });

  // Handle width change
  const handleWidthChange = (e) => {
    const newDisplayValue = e.target.value;
    setDisplayWidth(newDisplayValue);
    const backendValue = displayToBackend(newDisplayValue);
    setValue(`${name}.WIDTH`, backendValue, { shouldValidate: true });
  };

  // Handle height change
  const handleHeightChange = (e) => {
    const newDisplayValue = e.target.value;
    setDisplayHeight(newDisplayValue);
    const backendValue = displayToBackend(newDisplayValue);
    setValue(`${name}.HEIGHT`, backendValue, { shouldValidate: true });
  };

  // Update labels with display unit
  const widthLabel = displayUnit
    ? fields?.WIDTH?.label?.replace(/\(.*?\)/, `(${displayUnit})`) || `Breedte (${displayUnit})`
    : fields?.WIDTH?.label || 'Breedte (mm)';

  const heightLabel = displayUnit
    ? fields?.HEIGHT?.label?.replace(/\(.*?\)/, `(${displayUnit})`) || `Hoogte (${displayUnit})`
    : fields?.HEIGHT?.label || 'Hoogte (mm)';

  return (
    <div className="bg-white/60 rounded-2xl shadow-sm border border-gray-200/60 p-3.5 transition-all duration-200 hover:bg-white/80 hover:shadow-md backdrop-blur-sm">
      <div className="flex justify-between items-start mb-2">
        <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
        <AiOutlineInfoCircle className="text-gray-600 text-base cursor-pointer hover:text-gray-800 transition-colors flex-shrink-0" />
      </div>

      <p className="text-sm text-gray-600 mb-3">{detailed_label}</p>

      <div className="grid grid-cols-2 gap-3">
        <div className='w-full'>
          <label htmlFor={`${name}.WIDTH`} className="block mb-1.5 text-xs font-medium text-gray-700">
            {widthLabel}
          </label>
          <input
            id={`${name}.WIDTH`}
            type="number"
            value={displayWidth}
            onChange={handleWidthChange}
            className="bg-white/80 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-gray-400 focus:border-gray-400 hover:border-gray-400 block w-full p-2.5 placeholder-gray-400 transition-all duration-200 shadow-sm"
            placeholder={fields?.WIDTH?.placeholder || 'Breedte'}
            step={conversionFactor !== 1 ? "0.1" : "1"}
          />
          {/* Hidden input for form registration */}
          <input
            type="hidden"
            {...register(`${name}.WIDTH`, validation?.WIDTH)}
            value={displayToBackend(displayWidth)}
          />
          <ErrorBox key={1} errors={errors?.WIDTH} />
        </div>
        <div className='w-full'>
          <label htmlFor={`${name}.HEIGHT`} className="block mb-1.5 text-xs font-medium text-gray-700">
            {heightLabel}
          </label>
          <input
            id={`${name}.HEIGHT`}
            type="number"
            value={displayHeight}
            onChange={handleHeightChange}
            className="bg-white/80 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-gray-400 focus:border-gray-400 hover:border-gray-400 block w-full p-2.5 placeholder-gray-400 transition-all duration-200 shadow-sm"
            placeholder={fields?.HEIGHT?.placeholder || 'Hoogte'}
            step={conversionFactor !== 1 ? "0.1" : "1"}
          />
          {/* Hidden input for form registration */}
          <input
            type="hidden"
            {...register(`${name}.HEIGHT`, validation?.HEIGHT)}
            value={displayToBackend(displayHeight)}
          />
          <ErrorBox key={2} errors={errors?.HEIGHT} />
        </div>
      </div>
    </div>
  );
};

export default DimensionPicker;
